import React from 'react';

import { convertSnakeToCamelCase, round } from '@/lib/util';

import { FontAxes } from './types';

export const capitalizeFirstLetter = (string: string): string => {
  const input = string.replace(/\W/g, '');

  return input.charAt(0).toUpperCase() + input.slice(1);
};

export const mapVariableFontAxesToKeyAndDefaultValue = (fontAxes: FontAxes[]) => {
  const fontAxesKeyAndValue = fontAxes.reduce((acc, fontAxe) => {
    return { ...acc, [fontAxe.id]: fontAxe.default };
  }, {});

  return fontAxesKeyAndValue;
};

export const getVariableFontAxesActiveRange = (fontAxes: FontAxes): number => {
  const range = Math.abs(fontAxes.max) - Math.abs(fontAxes.min);

  if (range <= 1) return 0.1;
  if (range % 1 > 0) return round(range % 1, 1);

  return 1;
};

export const getVariableFontAxesStyle = (fontVariationSettings: {
  [key: string]: string | number;
}): string => {
  return `${Object.keys(fontVariationSettings)
    .map(
      (fontVariationSetting) =>
        `"${fontVariationSetting}" ${
          Array.isArray(fontVariationSettings[fontVariationSetting])
            ? fontVariationSettings[fontVariationSetting][1]
            : fontVariationSettings[fontVariationSetting]
        }`
    )
    .join(', ')}`;
};

export const getTextSectionStyle = (
  { fontFamily, fontSize, textAlign, fontVariationSettings },
  fontVariable = false
): React.CSSProperties =>
  fontVariable && fontVariationSettings
    ? {
        fontFamily,
        fontSize,
        textAlign,
        fontVariationSettings: getVariableFontAxesStyle(fontVariationSettings)
      }
    : {
        fontFamily,
        fontSize,
        textAlign
      };

// ref: https://convertingcolors.com/blog/article/convert_hex_to_rgb_with_javascript.html
export const hexToRgb = (hexcode: string) => {
  const aRgbHex = hexcode.replace('#', '').match(/.{1,2}/g);
  const aRgb = [parseInt(aRgbHex[0], 16), parseInt(aRgbHex[1], 16), parseInt(aRgbHex[2], 16)];

  return aRgb.join(', ');
};

// ref: https://gomakethings.com/converting-a-color-from-a-three-digit-hexcolor-to-a-six-digit-hexcolor-with-vanilla-js/
export const hex3ToHex6 = (hexcode: string) => {
  const hex3 = hexcode.replace('#', '');

  if (hex3.length !== 3) return hexcode;

  const hex6 = hex3.split('').reduce((acc, char) => {
    return `${acc}${char}${char}`;
  }, '');

  return `#${hex6}`;
};

export const GLYPH_SECTION_LABELS = {
  letter: 'Letters',
  number: 'Numerals',
  letterUppercase: 'Uppercase Letters',
  letterLowercase: 'Lowercase Letters',
  letterSuperscript: 'Superscript Forms',
  letterLigature: 'Ligatures',
  numberDecimalDigit: 'Digit Numerals',
  numberSmall: 'Numeral Derivatives',
  numberFraction: 'Fractions',
  punctuation: 'Punctuation',
  punctuationQuote: 'Quotation Marks',
  punctuationParenthesis: 'Parenthesis',
  punctuationDash: 'Dashes',
  symbol: 'Symbols',
  symbolCurrency: 'Currency Symbols',
  symbolMath: 'Math Symbols',
  symbolArrow: 'Arrows',
  symbolGeometry: 'Geometric Symbols',
  separator: 'Seperators',
  separatorSpace: 'Seperator Spaces',
  separatorFormat: 'Formatting Seperators',
  separatorNonspace: 'Nonspacing Seperators',
  markModifier: 'Marks',
  markSpacing: 'Accent Marks',
  markNonspacing: 'Combining Accent Marks',
  uncategorised: 'Miscellaneous'
};

export const getTranslatedCategoryLabel = (category: string, subCategory: string) => {
  const categoryLabel = `${category}${subCategory ? `-${subCategory}` : ''}`;
  const translatedCategoryLabel =
    GLYPH_SECTION_LABELS?.[
      convertSnakeToCamelCase(categoryLabel.replace(' ', '-').toLowerCase())
    ] || categoryLabel;

  return translatedCategoryLabel;
};
